import { useMemo, useState, useCallback } from 'react';
import { NodeSelection } from 'prosemirror-state';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { EditorActions } from '@atlaskit/editor-core/actions';
import type { Node } from '@atlaskit/editor-prosemirror/model';

import { CARDS_EXTENSION_TYPE } from '@atlassian/cards-extension/constants';
import { carouselExtensionType } from '@atlassian/carousel-extension/constants';
import { RenderVariant } from '@atlassian/company-hub-common/types';
import { SPOTLIGHT_EXTENSION_TYPE, SECTION_EXTENSION_TYPE } from '@atlassian/company-hub-common/constants';
import { SEARCH_EXTENSION_TYPE } from '@atlassian/custom-search-extension/constants';
import { LINKS_MENU_EXTENSION_TYPE } from '@atlassian/links-menu-extension/constants';

import type { ExtensionSource } from '@confluence/change-edition/entry-points/PremiumExtensionsUpsell.tsx';

import { HUB_ELEMENT_PLACEHOLDER_EXTENSION_TYPE } from '@atlassian/hub-element-placeholder/constants';

const ALLOWED_EXTENSIONS = [
	SPOTLIGHT_EXTENSION_TYPE,
	carouselExtensionType,
	CARDS_EXTENSION_TYPE,
	LINKS_MENU_EXTENSION_TYPE,
	SECTION_EXTENSION_TYPE,
	HUB_ELEMENT_PLACEHOLDER_EXTENSION_TYPE,
	SEARCH_EXTENSION_TYPE,
];

type useCustomExtensionConfigPanelResult = {
	activeNodeLocalId: string | null;
	openConfigPanel: (localId: string) => void;
	closeConfigPanel: (localId?: string) => void;
	upsellModalSource: ExtensionSource | undefined;
	setUpsellModalSource: (source: ExtensionSource | undefined) => void;
};

export const useCustomExtensionConfigPanel = (
	editorActions: EditorActions,
): useCustomExtensionConfigPanelResult => {
	const [activeNodeLocalId, setActiveNodeLocalId] = useState<string | null>(null);

	// when upsellModalSource is undefined, we use it to close the upsell modal in ModalUpsellWrapper.
	const [upsellModalSource, setUpsellModalSource] = useState<ExtensionSource | undefined>(
		undefined,
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const openConfigPanel = useCallback(
		(localId: string) => {
			if (!localId) {
				return;
			}

			const { attrs, type } = editorActions?.getNodeByLocalId(localId)?.toJSON() || {};
			const { extensionType, extensionKey } = attrs || {};

			const editorView = editorActions._privateGetEditorView();
			const selection = editorView?.state?.selection;

			if (selection instanceof NodeSelection) {
				if (ALLOWED_EXTENSIONS.includes(extensionType)) {
					setActiveNodeLocalId(localId);
				}
			} else {
				if (type !== 'multiBodiedExtension' || !editorView?.state) {
					return;
				}

				// Handle MultiBodiedExtension config panel
				let multiBodiedExtensionPosition = editorView.state.selection.from;
				editorView.state.tr.doc.descendants((node: Node, pos: number) => {
					if (node.attrs?.localId === localId) {
						multiBodiedExtensionPosition = pos;
						return;
					}
				});

				editorView.dispatch(
					editorView.state.tr?.setSelection?.(
						NodeSelection.create(editorView.state.tr.doc, multiBodiedExtensionPosition),
					),
				);

				if (ALLOWED_EXTENSIONS.includes(extensionType)) {
					setActiveNodeLocalId(localId);
				}
			}

			if (!!extensionKey && !!extensionType)
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'editExtension',
						source: `${extensionKey}ExtensionConfig`,
						attributes: {
							extensionType,
						},
					},
				}).fire();
		},
		[setActiveNodeLocalId, createAnalyticsEvent, editorActions],
	);

	const closeConfigPanel = useCallback(() => {
		const selectedNode = editorActions.getSelectedNode()?.toJSON();
		if (
			activeNodeLocalId &&
			selectedNode?.attrs?.localId === activeNodeLocalId &&
			selectedNode?.attrs?.extensionType === CARDS_EXTENSION_TYPE
		) {
			const parameters = selectedNode.attrs.parameters || {};

			if (parameters.renderVariant === RenderVariant.MANUAL) {
				delete parameters.filters;
				delete parameters.cardCount;
			}

			if (parameters.renderVariant === RenderVariant.DYNAMIC) {
				parameters.cards = [];
			}

			editorActions.replaceSelection(
				{
					...selectedNode,
					attrs: {
						...selectedNode.attrs,
						parameters: { ...parameters },
					},
				},
				true,
			);
		}

		setActiveNodeLocalId(null);
	}, [activeNodeLocalId, editorActions, setActiveNodeLocalId]);

	return useMemo(
		() => ({
			activeNodeLocalId,
			openConfigPanel,
			closeConfigPanel,
			upsellModalSource,
			setUpsellModalSource,
		}),
		[activeNodeLocalId, openConfigPanel, closeConfigPanel, upsellModalSource, setUpsellModalSource],
	);
};
